import { ActionTree } from 'vuex';
import ILocalState from './stateInterface';
import { AjaxService } from '@/services/AjaxService';
import { ajaxUrls } from '@/helpers/ajaxUrlsHelper';
import { ProductReport } from '@/interfaces/ProductReport';
import { FilterService } from '@/services/FilterService';
import { FilterData } from '@/interfaces/FilterData';
import { Jsona } from 'jsona';
import { TJsonApiBody, TJsonApiData } from 'jsona/lib/JsonaTypes';
import { SavedSearch } from '@/interfaces/SavedSearch';
import { eventBus, eventBusEvents } from '@/helpers/eventBusHelper';
import { isLastPage } from '@/helpers/resultEstimationHelper';
import { ProductGroupTypes } from '@/enums/ProductGroupTypes';

const dataFormatter = new Jsona();

const actions: ActionTree<ILocalState, {}> = {
    async getProductCodeSystemList(context, id?) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        let productCodeSystemList;
        if (token) {
            try {
                productCodeSystemList = await AjaxService.get(
                    ajaxUrls.search.cpcCodes(activeProductGroup, id),
                    token,
                );
            } catch (e) {
                return Promise.reject(e);
            }
        } else {
            context.dispatch('user/logoutUser', { root: true });
            throw new Error("Token doesn't exist");
        }
        context.commit('setProductCodeSystemList', productCodeSystemList);
        return Promise.resolve();
    },
    async searchForResults(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        let searchResults: TJsonApiBody;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        try {
            searchResults = (await AjaxService.get(
                ajaxUrls.search.query(
                    activeProductGroup,
                    payload.keywords,
                    payload.codes,
                    payload.page,
                ),
                token,
            )) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }

        const searchResultsData: TJsonApiData[] | null = searchResults.data
            ? (searchResults.data as TJsonApiData[])
            : null;

        context.commit(
            'setSearchKeywords',
            payload.keywords ? payload.keywords : '',
        );
        context.commit('setSearchCpcs', payload.codes ? payload.codes : '');
        if (searchResultsData) {
            searchResultsData.forEach((searchResult: TJsonApiData) => {
                if (
                    searchResult.attributes &&
                    searchResult.attributes.usdRevenues === '#VALUE!'
                ) {
                    searchResult.attributes.usdRevenues = '';
                }
            });
        }
        // @ts-ignore
        context.commit('setExpandViaCpcs', searchResults.meta.topCpcIds);
        // @ts-ignore
        context.commit('setTotalResults', searchResults.meta.totalItemsCount);
        context.commit('setSearchResults', searchResults);
        context.commit(
            'setFilteredSearchResults',
            context.getters.productSearchResults,
        );
        await context.dispatch('extractFilterOptions');
        return Promise.resolve();
    },
    async narrowSearchByKeyword(context, payload) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        let searchResults: TJsonApiBody;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        try {
            searchResults = (await AjaxService.get(
                ajaxUrls.search.query(
                    activeProductGroup,
                    payload.keywords,
                    payload.codes,
                    payload.narrowKeyword,
                ),
                token,
            )) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setSearchResults', searchResults);
        // @ts-ignore
        context.commit('setTotalResults', searchResults.meta.totalItemsCount);
        context.commit('setSearchCpcs', payload.codes ? payload.codes : '');
        context.commit(
            'setFilteredSearchResults',
            dataFormatter.deserialize(searchResults),
        );
        await context.dispatch('extractFilterOptions');
        return Promise.resolve();
    },

    extractFilterOptions(context) {
        const filterService = new FilterService(
            context.getters.productSearchResults,
        );
        const filterOptions = filterService.getFilterOptions();
        context.commit('setFilterOptions', filterOptions);
    },
    filterSearchResults(context, filterData: FilterData) {
        return new Promise(async (resolve) => {
            const filterService = new FilterService(
                context.getters.productSearchResults,
            );
            const filteredProductReports = await filterService.filterProductReports(
                filterData,
            );
            context.commit('setFilterData', filterData);
            context.commit('setFilteredSearchResults', filteredProductReports);
            resolve();
        });
    },
    async getSavedSearches(context) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let savedSearches;
        try {
            savedSearches = await AjaxService.get(
                ajaxUrls.search.savedSearchesWithGroup(activeProductGroup),
                token,
            );
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setSavedSearches', savedSearches);
        return Promise.resolve();
    },
    async addSavedSearch(context, title) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        const filterData = context.getters.filterData;
        const searchTerm = context.getters.searchKeywords;
        const searchCpcs = context.getters.searchCpcs.split(',').join(', ');
        const brandIds = context.getters.filteredSearchResults.map(
            (searchResult: ProductReport) => searchResult.id,
        );
        const payload = {
            data: {
                type: 'saved_searches',
                attributes: {
                    name: title,
                    prodGroup: activeProductGroup,
                    cpcCodes: searchCpcs === '' ? null : searchCpcs,
                    searchTerm,
                    keyword: filterData.keywordInput,
                    brandIds: brandIds.join(', '),
                    reasonsForValuation: filterData.valuationCauses.join(', '),
                    revenues: filterData.revenueRange.join(', '),
                    includeResultsWithoutRevenue:
                        filterData.withoutRevenueRecords,
                    revenueTypes: filterData.revenueTypes.join(', '),
                    years: filterData.years.join(', '),
                    countries: filterData.brandCountries.join(', '),
                },
            },
        };
        try {
            await AjaxService.post(
                ajaxUrls.search.savedSearches,
                payload,
                token,
            );
        } catch (e) {
            return Promise.reject(e);
        }
        await context.dispatch('getSavedSearches', activeProductGroup);
        return Promise.resolve();
    },
    async deleteSavedSearch(context, id) {
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        try {
            await AjaxService.delete(ajaxUrls.search.savedSearch(id), token);
        } catch (e) {
            return Promise.reject(e);
        }
        await context.dispatch('getSavedSearches', activeProductGroup);
        return Promise.resolve();
    },
    async getSavedSearch(context, id) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        const activeProductGroup =
            context.rootGetters['user/activeProductGroup'];
        if (!token) {
            context.dispatch('user/logoutUser', { root: true });
            return Promise.reject("Token doesn't exist");
        }
        let savedSearch;
        try {
            savedSearch = (await AjaxService.get(
                ajaxUrls.search.savedSearch(id),
                token,
            )) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        const deserializedData = dataFormatter.deserialize(
            savedSearch,
        ) as SavedSearch;
        const filterData: FilterData = {
            revenueRange: deserializedData.revenues
                .split(', ')
                .map((value) => Number(value)),
            years: deserializedData.years
                .split(', ')
                .map((value) => Number(value)),
            keywordInput: deserializedData.keyword,
            valuationCauses: deserializedData.reasonsForValuation.split(', '),
            withoutRevenueRecords:
                deserializedData.includeResultsWithoutRevenue,
            revenueTypes: deserializedData.revenueTypes.split(', '),
            brandCountries: deserializedData.countries.split(', '),
        };
        let searchKeywords = [
            deserializedData.searchTerm,
            deserializedData.keyword,
        ];
        searchKeywords = searchKeywords.filter((keyword) => keyword !== '');
        if (deserializedData.keyword !== '') {
            await context.dispatch('narrowSearchByKeyword', {
                keywords: searchKeywords.join(' AND '),
                codes: deserializedData.cpcCodes
                    ? deserializedData.cpcCodes.replace(' ', '')
                    : null,
            });
            context.commit('setSearchKeywords', deserializedData.searchTerm);
        } else {
            await context.dispatch('searchForResults', {
                keywords: deserializedData.searchTerm,
                codes: deserializedData.cpcCodes
                    ? deserializedData.cpcCodes.replace(' ', '')
                    : null,
            });
        }

        eventBus.$emit(eventBusEvents.injectFilterData, filterData);
        await context.dispatch('filterSearchResults', filterData);
        return Promise.resolve();
    },
    async downloadSavedSearch(context, id) {
        const token = context.rootGetters['user/token']
            ? context.rootGetters['user/token']
            : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject("Token doesn't exist");
        }
        let savedSearch: File;
        try {
            savedSearch = (await AjaxService.get(
                ajaxUrls.search.savedSearchDownload(id),
                token,
            )) as File;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(savedSearch);
    },
    setProductCodeSystemList(context) {
        return new Promise(async () => {
            context.commit('setProductCodeSystemList', null);
            Promise.resolve();
        });
    },
    async getProductTotalItems(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let productTotalItems: TJsonApiBody;
        try {
            productTotalItems =
                await AjaxService.get(ajaxUrls.search.productTotalItems(), token) as TJsonApiBody;

            let trademarkTotalItems = JSON.parse(JSON.stringify(productTotalItems));
            trademarkTotalItems.data = trademarkTotalItems.data.filter((item: any) => item.id === ProductGroupTypes.Trademark);
            context.commit('setTrademarkTotalItems', trademarkTotalItems);

            let customerTotalItems = JSON.parse(JSON.stringify(productTotalItems));
            customerTotalItems.data = customerTotalItems.data.filter((item: any) => item.id === ProductGroupTypes.CustomerRelations);
            context.commit('setCustomerTotalItems', customerTotalItems);

            let goodwillTotalItems = JSON.parse(JSON.stringify(productTotalItems));
            goodwillTotalItems.data = goodwillTotalItems.data.filter((item: any) => item.id === ProductGroupTypes.Goodwill);
            context.commit('setGoodwillTotalItems', goodwillTotalItems);

            let technologyTotalItems = JSON.parse(JSON.stringify(productTotalItems));
            technologyTotalItems.data = technologyTotalItems.data.filter((item: any) => item.id === ProductGroupTypes.Technology);
            context.commit('setTechnologyTotalItems', technologyTotalItems);

            let softwareTotalItems = JSON.parse(JSON.stringify(productTotalItems));
            softwareTotalItems.data = softwareTotalItems.data.filter((item: any) => item.id === ProductGroupTypes.Software);
            context.commit('setSoftwareTotalItems', softwareTotalItems);
        } catch (e) {
            return Promise.reject(e);
        }

        return Promise.resolve();
    },
    async getProductTotalItemsForAllGroups(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        return new Promise(async (resolve, reject) => {
            try {
                await context.dispatch('getProductTotalItems');
                return resolve();
            } catch (e) {
                return reject(e);
            }
        });
    },
};

export default actions;
